"use client";

import { useRef } from "react";
import Image from "next/image";
import { motion, useInView } from "framer-motion";
import { Globe, ShoppingBag, Smartphone, Server, Gauge, Monitor, Network } from "lucide-react";

type Service = {
  icon: React.ElementType;
  number: string;
  title: string;
  tagline: string;
  description: string;
  deliverables: string[];
  image: string;
};

const services: Service[] = [
  {
    icon: Globe,
    number: "01",
    title: "Web Design & Development",
    tagline: "Fast, beautiful, and built to convert.",
    description:
      "Custom websites and landing pages engineered from the ground up with Next.js, TypeScript, and Tailwind CSS. Every pixel is intentional — responsive across all screen sizes, accessible by default, and optimised for speed so visitors stay and act.",
    deliverables: [
      "Custom UI design & branding integration",
      "Responsive across all devices",
      "Next.js App Router with TypeScript",
      "Animations & micro-interactions",
      "CMS integration (Sanity, Contentful)",
      "Deployment to Vercel",
    ],
    image: "https://cdn.devbyskillz.com/projects/services/wed-dev.jpg",
  },
  {
    icon: ShoppingBag,
    number: "02",
    title: "E-Commerce Stores",
    tagline: "Stores that sell while you sleep.",
    description:
      "Full-stack e-commerce platforms with real-time inventory, multi-variant product listings, wishlist, cart, and secure checkout via Paystack, Stripe or bank transfer — plus an admin dashboard built for non-technical owners.",
    deliverables: [
      "Product catalogues with variants & filters",
      "Cart, wishlist & checkout flow",
      "Paystack, Stripe & bank transfer",
      "Order management & email notifications",
      "Customer auth & order history",
      "Admin dashboard for inventory",
    ],
    image: "https://cdn.devbyskillz.com/projects/services/onlineshop.jpg",
  },
  {
    icon: Smartphone,
    number: "03",
    title: "Mobile Apps",
    tagline: "Native speed. Cross-platform reach.",
    description:
      "Native iOS apps built with Swift, native Android with Kotlin, and cross-platform with React Native and Expo — all shipped to the App Store and Google Play. Whether you need one codebase or two fully native experiences, we build mobile products that feel fast and right.",
    deliverables: [
      "Native iOS development (Swift)",
      "Native Android development (Kotlin)",
      "Cross-platform (React Native + Expo)",
      "Push notifications & background tasks",
      "Offline-first data handling",
      "App Store & Play Store submission",
    ],
    image: "https://cdn.devbyskillz.com/projects/services/mobileapp.jpg",
  },
  {
    icon: Server,
    number: "04",
    title: "Full-Stack & Backend",
    tagline: "Backends that don't break.",
    description:
      "Robust APIs, databases, and server-side logic built with Node.js, Next.js API routes, and PostgreSQL / MongoDB — with authentication, role-based access, webhooks, and third-party integrations handled cleanly and securely.",
    deliverables: [
      "REST & tRPC API design",
      "Database modelling (PostgreSQL, MongoDB)",
      "Auth (JWT, OAuth, sessions)",
      "Role-based access control",
      "Third-party API integrations",
      "Serverless & edge-function deployment",
    ],
    image: "https://cdn.devbyskillz.com/projects/services/backend.jpg",
  },
  {
    icon: Gauge,
    number: "05",
    title: "SEO & Performance",
    tagline: "Rank higher. Load faster. Convert more.",
    description:
      "Technical SEO and Core Web Vitals optimisation so your site gets found and keeps visitors. Structured data, server-side metadata, image optimisation, caching strategies, and a clean analytics baseline to track what's working.",
    deliverables: [
      "On-page & technical SEO audit",
      "Server-side metadata & Open Graph",
      "Structured data (JSON-LD schema)",
      "Core Web Vitals optimisation (LCP, CLS, INP)",
      "Image & font optimisation",
      "Google Analytics / Plausible setup",
    ],
    image: "https://cdn.devbyskillz.com/projects/services/seo.jpg",
  },
  {
    icon: Monitor,
    number: "06",
    title: "Desktop Applications",
    tagline: "Powerful software for every platform.",
    description:
      "High-performance desktop applications built with Rust, Go, or Java — targeting macOS, Windows, and Linux. From lightweight system tools to full-featured GUI apps, we build software that runs close to the metal and ships with a polished experience.",
    deliverables: [
      "Rust (Tauri, CLI tools, system apps)",
      "Go (fast binaries, background services)",
      "Java (cross-platform GUI, enterprise apps)",
      "macOS, Windows & Linux targets",
      "System tray, notifications & file I/O",
      "Packaging & auto-update distribution",
    ],
    image: "https://cdn.devbyskillz.com/projects/services/desktop.jpg",
  },
  {
    icon: Network,
    number: "07",
    title: "Infrastructure & DevOps",
    tagline: "Ship faster. Stay online. Scale confidently.",
    description:
      "Server configuration, reverse proxying, CI/CD pipelines, and containerised deployments — everything between your codebase and production. Built for reliability: zero-downtime deploys, HTTPS everywhere, firewall rules, and automated rollbacks when things go wrong.",
    deliverables: [
      "Reverse proxy setup (Nginx, Caddy, Traefik)",
      "Docker & Docker Compose deployments",
      "CI/CD pipelines (GitHub Actions)",
      "VPS provisioning & hardening (UFW, SSH)",
      "SSL/TLS certificates & renewal",
      "Domain, DNS & subdomain routing",
    ],
    image: "https://cdn.devbyskillz.com/projects/services/dev-ops.jpg",
  },
];

const textContainer = {
  hidden: {},
  show: (delay: number) => ({
    transition: { staggerChildren: 0.1, delayChildren: delay },
  }),
};

const textItem = {
  hidden: { opacity: 0, y: 22 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] as [number, number, number, number] },
  },
};

const deliverableItem = {
  hidden: { opacity: 0, x: -12 },
  show: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] as [number, number, number, number] },
  },
};

function ServiceRow({ service, index }: { service: Service; index: number }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  const imageLeft = index % 2 === 0;
  const Icon = service.icon;

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 48 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] as [number, number, number, number] }}
      className="group grid grid-cols-1 overflow-hidden rounded-[2rem] border border-[var(--border)] bg-[var(--background)]/80 backdrop-blur-xl shadow-sm transition-all hover:border-[var(--accent)]/30 hover:shadow-md hover:shadow-[var(--accent)]/5 lg:grid-cols-2"
    >
      {/* Mobile-only header: icon + title shown ABOVE the image */}
      <div className="order-1 flex flex-col gap-4 p-8 pb-4 lg:hidden">
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[var(--accent)]/10 text-[var(--accent)]">
          <Icon size={26} />
        </div>
        <div className="flex flex-col gap-2">
          <p className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-[var(--accent)]">
            <span className="h-1 w-1 rounded-full bg-[var(--accent)]" />
            {service.tagline}
          </p>
          <h2 className="text-[1.75rem] font-extrabold leading-none tracking-tight text-[var(--foreground)]">
            {service.title}
          </h2>
        </div>
      </div>

      {/* Image */}
      <div
        className={`relative order-2 aspect-[4/3] w-full overflow-hidden bg-black/20 lg:aspect-auto lg:h-full ${
          imageLeft ? "lg:order-first" : "lg:order-last"
        }`}
      >
        <Image
          src={service.image}
          alt={service.title}
          fill
          sizes="(min-width: 1024px) 576px, 100vw"
          className="object-contain p-6 sm:p-10 transition-transform duration-700 ease-out"
          priority={index === 0}
          loading={index === 0 ? "eager" : "lazy"}
        />
        <div className="absolute inset-0 transition-colors duration-500 group-hover:bg-[var(--accent)]/5 mix-blend-overlay" />
      </div>

      {/* Content */}
      <motion.div
        custom={0.2}
        variants={textContainer}
        initial="hidden"
        animate={isInView ? "show" : "hidden"}
        className={`order-3 flex flex-col justify-center gap-8 p-8 sm:p-12 lg:p-14 ${
          imageLeft ? "lg:order-last" : "lg:order-first"
        }`}
      >
        {/* Desktop-only icon + heading (hidden on mobile — shown above via the header block) */}
        <div className="hidden flex-col gap-6 lg:flex">
          <motion.div
            variants={textItem}
            className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[var(--accent)]/10 transition-colors group-hover:bg-[var(--accent)] group-hover:text-white"
          >
            <Icon size={26} className="text-[var(--accent)] transition-colors group-hover:text-white" />
          </motion.div>
          <div className="flex flex-col gap-3">
            <motion.p variants={textItem} className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-[var(--accent)]">
              <span className="h-1 w-1 rounded-full bg-[var(--accent)]"></span>
              {service.tagline}
            </motion.p>
            <motion.h2
              variants={textItem}
              className="text-[1.75rem] font-extrabold leading-none tracking-tight text-[var(--foreground)] sm:text-3xl lg:text-[1.75rem] xl:text-4xl lg:whitespace-nowrap"
            >
              {service.title}
            </motion.h2>
          </div>
        </div>

        {/* Description */}
        <motion.p
          variants={textItem}
          className="text-base leading-relaxed text-[var(--foreground-muted)]"
        >
          {service.description}
        </motion.p>

        {/* Deliverables */}
        <motion.ul
          variants={textContainer}
          custom={0}
          className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2"
        >
          {service.deliverables.map((item) => (
            <motion.li
              key={item}
              variants={deliverableItem}
              className="flex items-center gap-3 rounded-full border border-[var(--border)] bg-[var(--foreground)]/[0.02] px-4 py-2 text-xs font-medium text-[var(--foreground-muted)] backdrop-blur-sm transition-colors hover:border-[var(--accent)] hover:bg-[var(--accent)]/5 hover:text-[var(--foreground)]"
            >
              <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--accent)]" />
              {item}
            </motion.li>
          ))}
        </motion.ul>
      </motion.div>
    </motion.div>
  );
}

export default function ServicesList() {
  return (
    <section className="mx-auto max-w-6xl px-4 pb-24 pt-10 sm:px-8 sm:pb-32">
      <div className="flex flex-col gap-10">
        {services.map((service, i) => (
          <ServiceRow key={service.number} service={service} index={i} />
        ))}
      </div>

      {/* Divider */}
      <div className="my-24 h-px w-full bg-gradient-to-r from-transparent via-[var(--border)] to-transparent" />

      {/* CTA */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] as [number, number, number, number] }}
        className="flex flex-col items-center gap-6 text-center"
      >
        <p className="text-xl font-bold tracking-tight text-[var(--foreground)] sm:text-2xl">
          Not sure which service fits your project?
        </p>
        <p className="max-w-2xl text-base leading-relaxed text-[var(--foreground-muted)]">
          Let&#39;s talk through your goals, infrastructure constraints, and timelines. We&#39;ll map out exactly what you need — no fluff, no upselling.
        </p>
        <a
          href="mailto:hello@devbyskillz.com"
          className="group relative mt-2 inline-flex items-center gap-3 overflow-hidden rounded-full bg-[var(--accent)] px-8 py-4 text-sm font-bold text-white shadow-lg shadow-[var(--accent)]/20 transition-all hover:-translate-y-1 hover:shadow-xl hover:shadow-[var(--accent)]/30 active:translate-y-0"
        >
          <span className="relative z-10">Get in touch</span>
          <div className="absolute inset-0 z-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 opacity-0 transition-opacity duration-500 group-hover:animate-shimmer group-hover:opacity-100" />
        </a>
      </motion.div>
    </section>
  );
}
